import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactComponent as CloseIcon } from '../../../../assets/SVG/close-circle-greay.svg';
import { ReactComponent as TaskAltIcon } from '../../../../assets/SVG/greenRigntWithBG.svg';
import { ReactComponent as PersonOutlineIcon } from '../../../../assets/SVG/orgPatientBG.svg';
import { ReactComponent as AssignmentTurnedInOutlinedIcon } from '../../../../assets/SVG/blueReqBg.svg';
import useCurrency from '../../../../components/CurrencyProvider';
import {
  modalHeaderSx,
  modalCloseIconSx,
  modalCloseSx,
  modalSaveSx,
} from '../../../Inventory/Common/inventory.helper';
import { CreateApprovalRuleFormValues } from './createApprovalRule.types';
import { useDocumentTypes } from '../inventoryApproval.queries';
import {
  previewModalContainerSx,
  previewHeaderRowSx,
  previewHeaderItemSx,
  previewHeaderLabelSx,
  previewHeaderValueSx,
  previewHeaderDividerSx,
  previewFlowContainerSx,
  previewFlowCardSx,
  previewFlowIconWrapperSx,
  previewFlowCardTitleSx,
  previewFlowCardSubTextSx,
  previewArrowSx,
  previewRequestIconSx,
  previewRoleIconSx,
  previewApprovedIconSx,
  previewFooterSx,
  previewFooterButtonsSx,
} from './CreateApprovalRuleSx';

interface PreviewWorkflowModalProps {
  open: boolean;
  onClose: () => void;
  values: CreateApprovalRuleFormValues;
  roleOptions: { label: string; value: string }[];
  onSave: () => void;
  isEditMode: boolean;
  onDisabled?: boolean;
}

const PreviewWorkflowModal: React.FC<PreviewWorkflowModalProps> = ({
  open,
  onClose,
  values,
  roleOptions,
  onSave,
  isEditMode,
  onDisabled = false,
}) => {
  const { currencySymbol, formatNumber } = useCurrency();
  const { t: tInventoryApproval } = useTranslation('inventory_approval_config');
  const { data: documentTypeOptions = [] } = useDocumentTypes();

  const docTypeLabel =
    documentTypeOptions.find((o) => o.id === values.documentType)?.name ?? values.documentType;

  const conditionText = (() => {
    const min = values.minAmount;
    const max = values.maxAmount;
    const isUnlimited = values.isUnlimited;

    if (min != null && (isUnlimited || max == null)) {
      return `> ${currencySymbol}${formatNumber(min)}`;
    }
    if (min == null && max != null) {
      return `< ${currencySymbol}${formatNumber(max)}`;
    }
    if (min != null && max != null) {
      return `${currencySymbol}${formatNumber(min)} - ${currencySymbol}${formatNumber(max)}`;
    }
    return '—';
  })();

  const getRoleName = (roleId: number | null) => {
    if (roleId == null) return '';
    return roleOptions.find((o) => o.value === String(roleId))?.label ?? '';
  };

  return (
    <Modal
      open={open}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick') onClose();
      }}
    >
      <Box sx={previewModalContainerSx}>
        {/* ── Header ── */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={1}
          px={2}
          mb={2}
          borderBottom="1px solid rgba(226, 227, 229, 1)"
        >
          <Typography sx={modalHeaderSx}>{tInventoryApproval('preview_workflow_title')}</Typography>
          <Tooltip title={tInventoryApproval('close')} arrow>
            <IconButton onClick={onClose} sx={modalCloseIconSx}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── Info row ── */}
        <Box sx={previewHeaderRowSx}>
          <Box sx={previewHeaderItemSx}>
            <Typography sx={previewHeaderLabelSx}>{tInventoryApproval('rule_name')}</Typography>
            <Typography sx={previewHeaderValueSx}>{values.ruleName || '—'}</Typography>
          </Box>
          <Box sx={previewHeaderDividerSx} />
          <Box sx={previewHeaderItemSx}>
            <Typography sx={previewHeaderLabelSx}>{tInventoryApproval('document_type')}</Typography>
            <Typography sx={previewHeaderValueSx}>{docTypeLabel || '—'}</Typography>
          </Box>
          <Box sx={previewHeaderDividerSx} />
          <Box sx={previewHeaderItemSx}>
            <Typography sx={previewHeaderLabelSx}>{tInventoryApproval('condition_trigger')}</Typography>
            <Typography sx={previewHeaderValueSx}>{conditionText}</Typography>
          </Box>
        </Box>

        {/* ── Workflow flow ── */}
        <Box sx={previewFlowContainerSx}>
          {/* Request Submit card */}
          <Box sx={previewFlowCardSx}>
            <Box sx={{ ...previewFlowIconWrapperSx, ...previewRequestIconSx }}>
              <AssignmentTurnedInOutlinedIcon />
            </Box>
            <Typography sx={previewFlowCardTitleSx}>{tInventoryApproval('request_submit')}</Typography>
          </Box>

          {/* Approval levels */}
          {values.approvalLevels.map((lvl) => {
            const roleName = getRoleName(lvl.role);
            const escalationRoleName = getRoleName(lvl.escalationRole);
            const topDetails: string[] = [];
            topDetails.push(tInventoryApproval('level_value', { level: lvl.level }));
            if (lvl.isMandatory) topDetails.push(tInventoryApproval('mandatory_toggle'));
            const escalationText =
              escalationRoleName && lvl.escalationTime
                ? tInventoryApproval('escalation_of', { role: escalationRoleName })
                : tInventoryApproval('no_escalations');

            return (
              <React.Fragment key={lvl.level}>
                <Typography sx={previewArrowSx}>→</Typography>
                <Box sx={previewFlowCardSx}>
                  <Box sx={{ ...previewFlowIconWrapperSx, ...previewRoleIconSx }}>
                    <PersonOutlineIcon  />
                  </Box>
                  <Typography sx={previewFlowCardTitleSx}>{roleName || tInventoryApproval('level_value', { level: lvl.level })}</Typography>
                  <Typography sx={previewFlowCardSubTextSx}>
                    {topDetails.join('  |  ')}
                  </Typography>
                  <Typography sx={previewFlowCardSubTextSx}>
                    {escalationText}
                  </Typography>
                </Box>
              </React.Fragment>
            );
          })}

          {/* Approved card */}
          <Typography sx={previewArrowSx}>→</Typography>
          <Box sx={previewFlowCardSx}>
            <Box sx={{ ...previewFlowIconWrapperSx, ...previewApprovedIconSx }}>
              <TaskAltIcon  />
            </Box>
            <Typography sx={previewFlowCardTitleSx}>{tInventoryApproval('approved')}</Typography>
          </Box>
        </Box>

        {/* ── Footer ── */}
        <Box sx={previewFooterSx}>
          <Box sx={previewFooterButtonsSx}>
            <Button variant="outlined" sx={modalCloseSx} onClick={onClose} data-testid="approval-preview-back-btn">
              {tInventoryApproval('back_to_edit')}
            </Button>
            <Button variant="contained" sx={modalSaveSx} onClick={onSave} disabled={onDisabled} data-testid="approval-preview-save-btn">
              {isEditMode ? tInventoryApproval('update_rule') : tInventoryApproval('save_rule')}
            </Button>
          </Box>

        </Box>
      </Box>
    </Modal>
  );
};

export default PreviewWorkflowModal;
